import { z } from 'zod';
import useSWR from 'swr';
import { get } from 'utils/axios';
import {
  PublicGetListingSessionsEndpoint,
  SessionStatus,
  SessionStatusFilter,
  ListingVersion,
} from '@kouto/types';
import { Session } from 'types/listings';
import { hasRemainingCapacity } from 'utils/session';

export interface PublicSessionsQueryParams {
  from?: string;
  to?: string;
  collectionId?: string | null;
  resourceGroupId?: string;
  experienceId?: string;
  latest?: boolean;
  skip?: boolean;
  maxSessionsPerDay?: number;
}

const useCollectionAvailableSessions = ({
  from,
  to,
  collectionId,
  resourceGroupId,
  experienceId,
  latest,
  skip = false,
  maxSessionsPerDay,
}: PublicSessionsQueryParams) => {
  let sessionsApiUrl: string | null = null;

  if (from && to && collectionId) {
    const queryParam: z.infer<
      typeof PublicGetListingSessionsEndpoint.querySchema
    > = {
      from,
      to,
      version: latest ? ListingVersion.LATEST : ListingVersion.PUBLISHED,
      // This hook only ever surfaces available sessions (see the client-side filters
      // below) — asking the server for the same predicate keeps the maxSessionsPerDay
      // cap from being filled by booked/overlapped rows.
      status: SessionStatusFilter.AVAILABLE,
    };
    if (resourceGroupId) {
      queryParam.resourceGroupId = resourceGroupId;
    }
    if (experienceId) {
      queryParam.experienceId = experienceId;
    }
    if (maxSessionsPerDay) {
      queryParam.maxSessionsPerDay = maxSessionsPerDay;
    }
    sessionsApiUrl = PublicGetListingSessionsEndpoint.url({
      listingId: collectionId,
      query: queryParam,
    });
  }

  // A new date, group or resource is a new key; the rows already on screen stay until the next arrive,
  // so a click never blanks the page. `isLoadingSessions` still says a request is in flight, for the
  // logic that must wait for fresh rows before it reads them.
  const response = useSWR(
    skip ? null : sessionsApiUrl,
    get<{ data: { sessions: Session[] } }>,
    { keepPreviousData: true },
  );

  let sessions = response.data?.data.sessions || [];
  sessions = sessions.filter((session) => !session.participantCount); // booked sessions
  // A cabana held through another group comes back unbooked but capped to full. Continuous spans are
  // carved server-side instead, so the cap never applies to them.
  sessions = sessions.filter(
    (session) =>
      session.isContinuousAvailability || hasRemainingCapacity(session),
  );
  sessions = sessions.filter(
    (session) => session.status !== SessionStatus.OVERLAPPED,
  ); // overlapped sessions
  sessions.sort((s1, s2) => (s1.startDateTime > s2.startDateTime ? 1 : -1));

  return {
    /** Whether any sessions response has arrived yet, previous or current; false only before the first. */
    hasLoadedSessions: response.data !== undefined,
    sessions,
    isLoadingSessions: response.isLoading || response.isValidating,
  };
};

export default useCollectionAvailableSessions;
